import { Auth } from '~/plugins/firebase-plugin.js';
import Cookies from 'js-cookie';

function buildUserObject (userData) {
	let { email, name, uid, photoURL } = userData;
	let user = {};
	user['email'] = email;
	user['name'] = name;
	user['uid'] = uid;
	user['picture'] = photoURL;
	return user;
}

export const state = () => ({
	user: null,
	uid: null,
	authError: null,
	isLoggingIn: false
});

export const getters = {
	isAuthenticated(state) {
		// The double-bang is not an operator; it coerces objects to Booleans.
		// Suppose state.user is an object:
		//  state.user = {...}
		//  !state.user == false
		//  !!state.user == true
		return !!state.user && !!state.user.uid;
	},
	guid(state) {
		if (state.user) {
			return state.user.guid;
		}
		return null;
	},
	isConversaEmployee(state) {
		if (state.user) {
			return state.user.isConversaEmployee || false;
		}
		return false;
	},
};

export const mutations = {
	setUser(state, user) {
		state.user = user;
	},
	setUid(state, uid) {
		state.uid = uid;
	},
	setAuthError(state, error) {
		state.authError = error;
	},
	setIsLoggingIn(state, isLoggingIn) {
		state.isLoggingIn = isLoggingIn;
	}
};

export const actions = {
	async refreshUser({dispatch}, userPayload) {
		await dispatch('clearError');

		try {
			// Now that we have the user, find the User in the Firestore database
			// which is where we keep track of siperLOCK GUID, etc
			let user = buildUserObject(userPayload);
            const userData = await this.$axios.$get('/api/admin/user-data', {params: {uid: user.uid}});

			// userData should contain properties from the Firestore database.
			user = {...user, ...userData};
			await dispatch('setUser', user);
			await dispatch('setUid', user.uid);
		} catch (error) {
			console.log('Error getting user from database: ', error.data);
			await dispatch('setError', {message: 'Sorry, try logging in again. If unable to login, please e-mail engineering@conversasolutions.com for support.'});
		}
	},
	async login({dispatch}, payload) {
		await dispatch('clearError');
		dispatch('setIsLoggingIn', true);

		Auth.signInWithEmailAndPassword(payload.email, payload.password)
		.then(async res => {
			try {
				let user = buildUserObject(res.user);
				const token = await Auth.currentUser.getIdToken(true);
				const userData = await this.$axios.$get('/api/admin/user-data', {params: {uid: user.uid}});

				// userData should contain properties from the Firestore database.
				user = {...user, ...userData};
				await dispatch('setUser', user);
				// await dispatch('setUid', user.uid);
				// https://github.com/js-cookie/js-cookie#expires
				// Can set the expiration for this cookie; otherwise,
				// this is a session cookie, deleted on browser exit.
				Cookies.set('access_token', token);
				dispatch('setIsLoggingIn', false);
				// await dispatch('setUser', user);
				await dispatch('setUid', token);
			} catch (error) {
                // console.log("Error getting user from database: ", error);
				dispatch('setIsLoggingIn', false);
				await dispatch('setError', error);
			}
		})
		.catch(error => {
            if (error.code && error.code === 'auth/user-not-found') {
                error.message = 'Incorrect email or password.';
            }
			dispatch('setIsLoggingIn', false);
			dispatch('setError', error);
		});
	},
	async deleteUser({dispatch}, userPayload) {
		const { uid, changedBy } = userPayload;
		if (!uid) {
			await dispatch('setError', 'Please specify a user to delete.');
			return;
		}

		try {
			await this.$axios.$delete(`/api/admin/users/${uid}`, {data: {changedBy: changedBy}});
		} catch (error) {
			this.$sentry.captureException(new Error(error));
			await dispatch('setError', error.data.message);
		}
	},
	async sendPasswordResetEmail({dispatch}, email) {
		await dispatch('clearError');
		try {
			await Auth.sendPasswordResetEmail(email);
		} catch (error) {
			await dispatch('setError', error);
		}
	},
	async logout({dispatch}) {
		await Auth.signOut();
		Cookies.remove('access_token');
		await dispatch('setUser', null);
		await dispatch('setUid', null);
		// eslint-disable-next-line no-undef
		$nuxt._router.push('/');
	},
	setIsLoggingIn({commit}, isLoggingIn) {
		commit('setIsLoggingIn', isLoggingIn);
	},
	clearError({commit}) {
		commit('setAuthError', null);
	},
	setUser({commit}, user) {
		commit('setUser', user);
	},
	setUid({commit}, uid) {
		commit('setUid', uid);
	},
	setError({commit}, error){
		commit('setAuthError', error);
	},
	async saveUser({dispatch, state}, payload) {
		dispatch('clearError');

		const { editedUser, currentUser, updatedAttributes } = payload;

		try {
			// eslint-disable-next-line no-unused-vars
			const user = await this.$axios.$put(`/api/admin/users/${editedUser.uid}`, {...updatedAttributes, email: editedUser.email, changedBy: currentUser.email});
			if (editedUser.uid === currentUser.uid) {
				const updatedUser = {...state.user, ...updatedAttributes};
				dispatch('setUser', updatedUser);
			}
		} catch (error) {
			dispatch('setError', error.data.message);
		}
	},
	async changePassword({dispatch}, payload) {
		const { email, currentPassword, uid, newPassword } = payload;

		Auth.signInWithEmailAndPassword(email, currentPassword)
		.then(async res => {
			try {
				await this.$axios.$put(`/api/admin/users/${uid}`, { newPassword: newPassword, email: email, changedBy: email});
				console.log('Password change successful for ', res.user.email);
			} catch (error) {
				dispatch('setIsLoggingIn', false);
				await dispatch('setError', error.data.message);
			}

		})
		.catch(async error => {
			dispatch('setIsLoggingIn', false);
			await dispatch('setError', error);
		});
	},
};

export const strict = false;
